import { useState, useRef, useMemo, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ReactGrid } from "@silevis/reactgrid";
import { parse, format, subYears } from "date-fns";
import { Input, Button, Pagination, FormControl } from "@hydra/atom/components";
import { useQuery } from "@tanstack/react-query";
import qs from "qs";
import { formatApiPayloadDate } from "@/utils/helpers";
import { Select } from "@/components/common";
import { useDebounce } from "@/hooks";
import {
  SvgIcon,
  CustomDropdown,
  IconButton,
  CustomizedDateRangePicker,
  CustomizedDatePicker,
  NoDataFound,
} from "@/components/common";
import TableSkeleton from "@/components/common/TableSkeleton";
import { ReportFilterDrawer } from "@/components/drawers";
import appSettings from "@/settings";
import { mergeDataByMonth } from "@/components/finance/reporting/helpers";
import { ReportCard } from "@/components/property";
import { getAccountingPeriods } from "@/api/finance/accountingPeriodApi";
import { getCompanies } from "@/api/finance/companyApi";
import { getAccountingMonths } from "@/utils/report/constants";

const perPageOptions = [
  { label: "50", value: 50 },
  { label: "100", value: 100 },
  { label: "150", value: 150 },
];

function ReportCardContainer({ reportCardData }) {
  const { size, data, dataChunks } = reportCardData;

  const chunkedData = [];
  for (let i = 0; i < data.length; i += dataChunks) {
    chunkedData.push(data.slice(i, i + dataChunks));
  }

  return (
    <div className="cards-container">
      {chunkedData.slice(0, size).map((chunk, index) => (
        <div className="card-container" key={index}>
          <ReportCard data={chunk} />
        </div>
      ))}
    </div>
  );
}

function DynamicReport({
  reportName,
  activeCompany,
  columnsDataProvider,
  rowsDataProvider,
  fetchData,
  exportUrl,
  filtersInitialState,
  filterOptions,
  orderBy,
  showDateRangeFilter = true,
  startDate = subYears(new Date(), 1),
  endDate = new Date(),
  isPaginated = true,
  showDateFilter = false,
  reportCardData,
  onDataLoaded,
  showLoadReportButton = false,
  accountingPeriodEnabled = false,
  showSearchFilter = true,
}) {
  const ref = useRef();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [columnsData, setColumnsData] = useState(columnsDataProvider);
  const [perPage, setPerPage] = useState(perPageOptions[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState(filtersInitialState);
  const debouncedFilter = useDebounce(filters, 600);
  const [shouldRunReport, setShouldRunReport] = useState(false);
  const [accountingPeriod, setAccountingPeriod] = useState(null);
  const [accountingMonth, setAccountingMonth] = useState(null);
  const [company, setCompany] = useState({
    label: activeCompany?.name,
    value: activeCompany?.id,
  });
  const initializeDateRange = () => {
    const urlStartDate = searchParams.get("startDate");
    const urlEndDate = searchParams.get("endDate");

    if (urlStartDate && urlEndDate) {
      try {
        const parsedStartDate = parse(urlStartDate, "dd-MM-yyyy", new Date());
        const parsedEndDate = parse(urlEndDate, "dd-MM-yyyy", new Date());

        if (!Number.isNaN(parsedStartDate) && !Number.isNaN(parsedEndDate)) {
          return [parsedStartDate, parsedEndDate];
        }
      } catch (error) {
        console.error("Error parsing dates from URL:", error);
      }
    }
    return [startDate, endDate];
  };

  const [dateRange, setDateRange] = useState(initializeDateRange());

  const handleDateRangeChange = (value) => {
    setDateRange(value);
    setShouldRunReport(false);
    if (value && value.length === 2 && value[0] && value[1]) {
      const formattedStartDate = format(value[0], "dd-MM-yyyy");
      const formattedEndDate = format(value[1], "dd-MM-yyyy");

      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("startDate", formattedStartDate);
      newSearchParams.set("endDate", formattedEndDate);

      navigate(`?${newSearchParams.toString()}`, { replace: true });
    }
  };
  const [filterText, setFilterText] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const initializeDate = () => {
    const urlDate = searchParams.get("date");

    if (urlDate) {
      try {
        const parsedDate = parse(urlDate, "dd-MM-yyyy", new Date());
        if (!Number.isNaN(parsedDate)) {
          return parsedDate;
        }
      } catch (error) {
        console.error("Error parsing date from URL:", error);
      }
    }
    return new Date();
  };

  const [date, setDate] = useState(initializeDate());

  const handleDateChange = (value) => {
    setDate(value);
    setShouldRunReport(false);
    if (value) {
      const formattedDate = format(value, "dd-MM-yyyy");
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("date", formattedDate);
      navigate(`?${newSearchParams.toString()}`, { replace: true });
    }
  };

  const columns = useMemo(
    () => columnsData.filter((c) => c.value),
    [columnsData],
  );
  const handlePagination = (pageNumber, pageSize) => {
    setCurrentPage(pageNumber);
    setPerPage(pageSize);
  };

  const formatFilters = (data) => {
    let filterData = { ...filtersInitialState };
    if (!data) {
      return {};
    }

    if (showDateRangeFilter) {
      filterData = {
        StartDate: formatApiPayloadDate(dateRange[0]),
        EndDate: formatApiPayloadDate(dateRange[1]),
        ...filtersInitialState,
      };
    }

    if (showDateFilter) {
      filterData.Date = formatApiPayloadDate(date);
    }

    filterOptions?.forEach((option) => {
      if (data[option.key]) {
        let optionValue;
        if (option?.keyType === "label") {
          optionValue = data[option.key].label;
        } else if (option?.keyType === "string") {
          optionValue = data[option.key];
        } else if (option?.keyType === "number") {
          optionValue = data[option.key].number;
        } else if (option?.keyType === "date") {
          optionValue = formatApiPayloadDate(data[option.key]);
        } else if (option?.keyType === "array") {
          optionValue = data[option.key].map((o) => o.value);
        } else {
          optionValue = data[option.key].value || data[option.key].id;
        }
        if (option.additionalFilterKey) {
          if (!filterData[option.additionalFilterKey]) {
            filterData[option.additionalFilterKey] = {};
          }
          filterData[option.additionalFilterKey][option.apiKey] = optionValue;
        } else {
          filterData[option.apiKey] = optionValue;
        }
      } else {
        filterData[option.apiKey] = filtersInitialState[option.apiKey];
      }
    });

    if (reportName === "journal-ledger") {
      filterData = {
        ...filterData,
        scope: "leasing",
        "filters.accountCode": filterData.AccountCode,
        "filters.costCenters": filterData.Component,
      };
    }

    return filterData;
  };

  const isDateRangeValid = useMemo(() => {
    if (!dateRange || !Array.isArray(dateRange)) return false;
    if (dateRange.length !== 2) return false;

    const [fromDate, toDate] = dateRange;
    return (
      Boolean(fromDate) &&
      Boolean(toDate) &&
      !Number.isNaN(new Date(fromDate)) &&
      !Number.isNaN(new Date(toDate))
    );
  }, [dateRange]);

  const additionalFilters = () => {
    if (accountingPeriodEnabled || reportName !== "trail-balance") {
      return {
        CompanyId: company?.value,
      };
    }
    return {};
  };

  const exportExcel = () =>
    window.open(
      `${appSettings.baseUrl}/${exportUrl}?${qs.stringify(
        formatFilters(debouncedFilter),
      )}&${qs.stringify(additionalFilters())}`,
      "_blank",
      "noopener,noreferrer",
    );

  const { data: reportData, isLoading } = useQuery(
    [
      reportName,
      currentPage,
      perPage.value,
      debouncedFilter,
      dateRange,
      date,
      shouldRunReport,
      accountingMonth,
      accountingPeriod,
      company,
    ],
    () =>
      fetchData(
        isPaginated
          ? {
              pageNumber: currentPage,
              pageSize: perPage.value,
              orderBy,
              ...formatFilters(debouncedFilter),
              ...additionalFilters(),
            }
          : {
              orderBy,
              ...formatFilters(debouncedFilter),
              ...additionalFilters(),
            },
      ),
    {
      enabled: showLoadReportButton
        ? shouldRunReport && isDateRangeValid
        : isDateRangeValid,
    },
  );

  const totalCount = useMemo(() => reportData?.totalCount || 0, [reportData]);

  useEffect(() => {
    if (reportData?.data && onDataLoaded) {
      onDataLoaded(reportData?.data);
    }
  }, [reportData]);

  const formatReport = (transaction) => {
    let formattedData = transaction;
    if (reportName === "building-revenue" && formattedData?.length > 0) {
      formattedData = mergeDataByMonth(transaction);
    }

    if (filterText) {
      return formattedData?.filter((obj) =>
        Object.values(obj).some((value) =>
          value?.toString().toLowerCase().includes(filterText.toLowerCase()),
        ),
      );
    }
    return formattedData;
  };

  const onClose = (value) => {
    setIsOpen(value);
  };

  const handleColumnResize = (ci, width) => {
    setColumnsData((prevColumns) => {
      const columnIndex = prevColumns.findIndex((el) => el.columnId === ci);
      const resizedColumn = prevColumns[columnIndex];
      const updatedColumn = { ...resizedColumn, width };
      prevColumns[columnIndex] = updatedColumn;
      return [...prevColumns];
    });
  };

  const rows = useMemo(
    () =>
      rowsDataProvider({
        data: {
          data: formatReport(reportData?.data) || [],
        },
        columns,
      }),
    [reportData, columns, filterText],
  );

  const handleRunReport = () => {
    setShouldRunReport(true);
  };

  const handleAccountingPeriodChange = (value) => {
    setAccountingMonth(value);
    if (value && value.value) {
      const currentYear = new Date().getFullYear();
      const monthNumber = value.value;
      const startOfMonth = new Date(currentYear, monthNumber - 1, 1);
      const endOfMonth = new Date(currentYear, monthNumber, 0);
      setDateRange([startOfMonth, endOfMonth]);
      const formattedStartDate = format(startOfMonth, "dd-MM-yyyy");
      const formattedEndDate = format(endOfMonth, "dd-MM-yyyy");

      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("startDate", formattedStartDate);
      newSearchParams.set("endDate", formattedEndDate);

      navigate(`?${newSearchParams.toString()}`, {
        replace: true,
      });
    }
  };

  return (
    <>
      {reportCardData && (
        <ReportCardContainer reportCardData={reportCardData} />
      )}
      <div className="table-wrapper report-sheet" ref={ref}>
        <div className="filter-row">
          <div className="filter-left-section">
            {showSearchFilter && (
              <div className="col-filter search-bar-report ">
                <Input
                  className="input-height-fix "
                  value={filterText}
                  onChange={setFilterText}
                  placeholder="Search by name"
                />
              </div>
            )}
            {showDateRangeFilter && (
              <div className="col-filter general-date-range">
                <CustomizedDateRangePicker
                  name="dateRange"
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  required
                />
              </div>
            )}
            {showDateFilter && (
              <div className="col-filter general-date-range">
                <FormControl>
                  <CustomizedDatePicker
                    name="date"
                    value={date}
                    onChange={handleDateChange}
                  />
                </FormControl>
              </div>
            )}
          </div>

          <div className="filter-right-section">
            <div className="col-filter">
              <IconButton
                className="icon-button-with-text"
                onClick={() => exportExcel()}
                disabled={!isDateRangeValid}
              >
                <SvgIcon icon="excel" />
                <span>Download Excel</span>
              </IconButton>
            </div>
            <div className="col-filter">
              <CustomDropdown
                data={columnsData}
                setColumnData={setColumnsData}
              />
            </div>
            <div className="col-filter">
              {filterOptions && (
                <>
                  <IconButton
                    className="icon-button-with-text"
                    onClick={() => setIsOpen(true)}
                    small
                    bordered
                  >
                    <SvgIcon icon="filter-lines" />
                  </IconButton>
                  <ReportFilterDrawer
                    filters={filters}
                    setFilters={setFilters}
                    type={reportName}
                    size={400}
                    isOpen={isOpen}
                    onClose={onClose}
                  />
                </>
              )}
            </div>
          </div>
        </div>
        <div className="filter-row">
          {accountingPeriodEnabled && (
            <>
              <div className="col-filter company-col ">
                <Select
                  id="accountingMonth"
                  name="accountMonth"
                  placeholder="Select accounting month"
                  queryKey="accounting-month"
                  value={accountingMonth}
                  onChange={(value) => {
                    handleAccountingPeriodChange(value);
                  }}
                  optionsLoader={getAccountingMonths}
                  dataAccessKey="data"
                  optionLabelKey="name"
                  optionValueKey="id"
                  isClearable
                  backspaceRemovesValue
                  rules="required"
                />
              </div>
              <div className="col-filter company-col">
                <Select
                  name="company"
                  queryKey="company"
                  placeholder="Select company"
                  value={company}
                  onChange={(value) => setCompany(value)}
                  optionsLoader={() => getCompanies()}
                  dataAccessKey="data"
                  optionLabelKey="name"
                  optionValueKey="id"
                  isClearable
                  backspaceRemovesValue
                />
              </div>
            </>
          )}
        </div>
        <div className="react-grid-container">
          {showLoadReportButton ? (
            <>
              {isDateRangeValid && shouldRunReport ? (
                !isLoading ? (
                  <ReactGrid
                    rows={rows}
                    columns={columns}
                    enableFillHandle
                    enableRangeSelection
                    onColumnResized={handleColumnResize}
                    disableVirtualScrolling
                  />
                ) : (
                  <TableSkeleton />
                )
              ) : (
                <div className="report-loading-btn">
                  <NoDataFound
                    title={
                      isDateRangeValid
                        ? "Click on Execute Report to view the data"
                        : "Please select a date range first"
                    }
                  />
                  <IconButton
                    onClick={handleRunReport}
                    disabled={!isDateRangeValid}
                    className="icon-button-with-text"
                  >
                    <SvgIcon icon="excel" />
                    <span>Execute Report</span>
                  </IconButton>
                </div>
              )}
            </>
          ) : (
            <>
              {isDateRangeValid ? (
                !isLoading ? (
                  <ReactGrid
                    rows={rows}
                    columns={columns}
                    enableFillHandle
                    enableRangeSelection
                    onColumnResized={handleColumnResize}
                    disableVirtualScrolling
                  />
                ) : (
                  <TableSkeleton />
                )
              ) : (
                <NoDataFound title="Please select a date range" />
              )}
            </>
          )}
        </div>
        {!isLoading &&
          isPaginated &&
          isDateRangeValid &&
          (!showLoadReportButton || shouldRunReport) && (
            <Pagination
              className="dashboard-pagination"
              options={perPageOptions}
              perPage={perPage}
              onSelectChange={(val) => handlePagination(currentPage, val)}
              pageRangeDisplayed={3}
              pageNo={currentPage}
              handlePageClick={(pageNo) =>
                handlePagination(pageNo.selected + 1, perPage)
              }
              showResults
              offset={0}
              totalItems={totalCount}
              pageCount={Math.ceil(totalCount / perPage.value)}
              reactPaginateProps={{
                previousLabel: <span className="material-icons">&#xe5cb;</span>,
                nextLabel: <span className="material-icons">&#xe5cc;</span>,
                forcePage: currentPage - 1,
              }}
            />
          )}
      </div>
    </>
  );
}

export default DynamicReport;
